/**
 * Bridges between the DrugScheduleEditor's AddSheet body shape (what
 * the pharmacy-dose-schedule-api /dose/create endpoint expects on the
 * standalone patient page) and the AI-transcript captured-item payload
 * (what api-ai-transcript-v2-service stores in /captured until the
 * reviewer clicks Save & next, at which point the Go writeDrugSchedule
 * fan-out finally hits the pharmacy service).
 *
 * The mapping preserves everything the reviewer typed — including any
 * variable per-dose gap — by carrying the fully-expanded doses[] array
 * across to the captured payload. The Go writeDrugSchedule handler
 * short-circuits its atom-expansion path when it sees a doses[] key on
 * the payload (see api-ai-transcript-v2-service/service/adapters/
 * drug_schedule.go), so a manual-captured plan lands on pharmacy with
 * exactly the dates the reviewer saw.
 *
 * Atoms are also emitted (drug_name / start_date / num_doses /
 * interval_value / interval_unit) so the ReviewSave DrugScheduleTable
 * can render a short summary line without having to inspect doses[].
 */

/**
 * DrugScheduleEditor.AddSheet body → captured "add" payload.
 *
 * body shape (from AddSheet.submit()):
 *   {
 *     pid, status: "Active",
 *     doses: [
 *       { doseno, dosename, planneddate, rescheduledate: "", actualdate: "",
 *         nextdosefrequency: { unit: "days", value: "<gap-days>" | "" } },
 *       ...
 *     ],
 *     createdby: "<display name>",
 *   }
 */
export function bodyToCapturedAdd(body) {
  const b = body || {};
  const doses = Array.isArray(b.doses) ? b.doses : [];
  const first = doses[0] || {};
  const firstGap = first.nextdosefrequency && first.nextdosefrequency.value;
  const firstUnit = (first.nextdosefrequency && first.nextdosefrequency.unit) || "days";
  return {
    // Atoms — mirror the AI's add_drug_schedule tool payload so
    // ReviewSave can render summary consistently and the Go adapter's
    // atom path still works when doses[] is absent.
    drug_name:       first.dosename || "",
    start_date:      first.planneddate || "",
    num_doses:       doses.length,
    interval_value:  Number(firstGap) || 1,
    interval_unit:   firstUnit,
    // Full pharmacy body — the Go writeDrugSchedule handler prefers
    // this when present so per-dose gaps typed by the reviewer survive
    // review-save into the pharmacy DB verbatim.
    pid:             b.pid,
    status:          b.status,
    doses:           doses,
    createdby:       b.createdby,
  };
}
